// Shared recharts formatting helpers for the app's month-bucketed bar/area
// charts (billing overview, practice overview) - extracted so a second chart
// doesn't reimplement the same "2026-01" -> "Jan '26" label logic.

// "2026-01" -> "Jan"; January gets the year for orientation across ranges.
export function fmtMonth(key: string): string {
    const [year, month] = key.split('-').map(Number);
    const label = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
        month: 'short',
        timeZone: 'UTC'
    });
    return month === 1 ? `${label} '${String(year).slice(2)}` : label;
}

export const AXIS_TICK = { fontSize: 11, fill: '#9ca3af' } as const;
export const HOVER_CURSOR = { fill: 'rgba(17, 24, 39, 0.04)' } as const;

// ResponsiveContainer's `debounce` is a trailing throttle; matching the 300ms sidebar
// fold redraws each chart once or twice per fold, always ending at the final width.
export const CHART_RESIZE_DEBOUNCE_MS = 300;

export type ChartBucketGranularity = 'day' | 'week' | 'month' | 'year';

// Buckets are ISO-anchored ("2026-07-28" for day/week, "2026-07" for month,
// "2026" for year) - the leading Y[-M[-D]] is parsed and rebuilt as a UTC
// date so the label never shifts a day in negative-offset timezones (same
// approach as utils/dates.ts's formatDateOnly).
export function parseBucketDate(bucket: string): Date | null {
    const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(bucket);
    if (!match) return null;
    const [, yearStr, monthStr, dayStr] = match;
    const date = new Date(
        Date.UTC(Number(yearStr), Number(monthStr ?? '1') - 1, Number(dayStr ?? '1'))
    );
    return Number.isNaN(date.getTime()) ? null : date;
}

export function formatBucketLabel(bucket: string, granularity: ChartBucketGranularity): string {
    const date = parseBucketDate(bucket);
    if (!date) return bucket;
    if (granularity === 'year') return String(date.getUTCFullYear());
    if (granularity === 'month') {
        return date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
